import { useState } from 'react';

import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Clock, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { formatDateTime } from '@/utils/format';

import type { CallReview } from '@/hooks/useReviews';
import type { ReactElement } from 'react';

// --- Utility functions ---

/** Convert snake_case / camelCase keys to readable labels */
function formatKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Deep equality check for comparison highlighting */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a == b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;
  const aKeys = Object.keys(aObj);
  const bKeys = Object.keys(bObj);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((k) => deepEqual(aObj[k], bObj[k]));
}

// --- Data section discovery ---

interface DataSection {
  key: string;
  label: string;
  original: unknown;
  reviewed: unknown;
  matches?: boolean;
}

/** Auto-discover original / reviewed data pairs from a CallReview */
function getDataSections(callReview: CallReview): DataSection[] {
  const sections: DataSection[] = [];
  const skipBases = new Set(['outcome', 'status']);
  const seenBases = new Set<string>();
  const cr = callReview as unknown as Record<string, unknown>;

  console.log({ callReview });

  for (const key of Object.keys(cr)) {
    let baseName: string | null = null;
    if (key.startsWith('original_')) baseName = key.slice('original_'.length);
    else if (key.startsWith('reviewed_')) baseName = key.slice('reviewed_'.length);
    if (!baseName || skipBases.has(baseName) || seenBases.has(baseName)) continue;
    seenBases.add(baseName);

    const original = cr[`original_${baseName}`];
    const reviewed = cr[`reviewed_${baseName}`];
    const matches = cr[`${baseName}_matches`];
    if (original == null && reviewed == null) continue;

    sections.push({
      key: baseName,
      label: formatKey(baseName),
      original: original ?? null,
      reviewed: reviewed ?? null,
      matches: typeof matches === 'boolean' ? matches : undefined,
    });
  }

  return sections;
}

// --- Generic recursive data renderer ---

interface DataRendererProps {
  data: unknown;
  /** Pass the original value to highlight differences */
  compareWith?: unknown;
  depth?: number;
}

const DataRenderer = ({ data, compareWith, depth = 0 }: DataRendererProps): ReactElement => {
  // null / undefined
  if (data === null || data === undefined) {
    return <span className="text-muted-foreground text-xs italic">N/A</span>;
  }

  // boolean
  if (typeof data === 'boolean') {
    const changed = compareWith !== undefined && !deepEqual(data, compareWith);
    return (
      <span
        className={`text-sm ${changed ? 'rounded bg-amber-100 px-1.5 py-0.5 dark:bg-amber-900/50' : ''}`}
      >
        {data ? 'true' : 'false'}
      </span>
    );
  }

  // number
  if (typeof data === 'number') {
    const changed = compareWith !== undefined && !deepEqual(data, compareWith);
    return (
      <span
        className={`text-sm tabular-nums ${changed ? 'rounded bg-amber-100 px-1.5 py-0.5 dark:bg-amber-900/50' : ''}`}
      >
        {data}
      </span>
    );
  }

  // string
  if (typeof data === 'string') {
    if (data === '') {
      return <span className="text-muted-foreground text-xs italic">empty</span>;
    }
    const changed = compareWith !== undefined && !deepEqual(data, compareWith);
    return (
      <span
        className={`text-sm ${changed ? 'rounded bg-amber-100 px-1.5 py-0.5 dark:bg-amber-900/50' : ''}`}
      >
        {data}
      </span>
    );
  }

  // array
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <span className="text-muted-foreground text-xs italic">Empty list</span>;
    }
    const compareArr = Array.isArray(compareWith) ? compareWith : undefined;
    return (
      <div
        className={`space-y-1 ${depth > 0 ? 'border-l-2 border-slate-200 pl-3 dark:border-slate-700' : ''}`}
      >
        {data.map((item, idx) => (
          <div key={idx} className="flex items-start gap-1.5">
            <span className="text-muted-foreground mt-0.5 text-xs select-none">•</span>
            <div className="min-w-0 flex-1">
              <DataRenderer data={item} compareWith={compareArr?.[idx]} depth={depth + 1} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // object
  if (typeof data === 'object') {
    const entries = Object.entries(data as Record<string, unknown>);
    if (entries.length === 0) {
      return <span className="text-muted-foreground text-xs italic">Empty</span>;
    }
    const compareObj =
      compareWith && typeof compareWith === 'object' && !Array.isArray(compareWith)
        ? (compareWith as Record<string, unknown>)
        : undefined;

    return (
      <div
        className={`space-y-1.5 ${depth > 0 ? 'border-l-2 border-slate-200 pl-3 dark:border-slate-700' : ''}`}
      >
        {entries.map(([key, value]) => {
          const origValue = compareObj?.[key];
          const isComplex = value !== null && typeof value === 'object';
          return (
            <div key={key}>
              <span className="text-muted-foreground text-xs font-medium">{formatKey(key)}:</span>
              {isComplex ? (
                <div className="mt-0.5">
                  <DataRenderer data={value} compareWith={origValue} depth={depth + 1} />
                </div>
              ) : (
                <>
                  {' '}
                  <DataRenderer data={value} compareWith={origValue} depth={depth + 1} />
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // fallback
  return <span className="text-sm">{String(data)}</span>;
};

// --- Shared review content (used by both compact and regular modes) ---

const ReviewContent = ({
  callReview,
  confidenceScore,
  hasIssues,
}: {
  callReview: CallReview;
  confidenceScore: number;
  hasIssues: boolean;
}) => {
  const dataSections = getDataSections(callReview);

  return (
    <>
      {/* Confidence Score */}
      <div className="flex items-center justify-between rounded-lg bg-slate-50 p-4 dark:bg-slate-800">
        <span className="text-sm font-medium">AI Confidence Score</span>
        <div className="flex items-center gap-2">
          <div className="h-2 w-32 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
            <div
              className={`h-full ${
                confidenceScore >= 0.8
                  ? 'bg-emerald-500'
                  : confidenceScore >= 0.6
                    ? 'bg-amber-500'
                    : 'bg-rose-500'
              }`}
              style={{ width: `${confidenceScore * 100}%` }}
            />
          </div>
          <span className="text-sm font-semibold">{Math.round(confidenceScore * 100)}%</span>
        </div>
      </div>

      {/* Original vs Reviewed Data */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Original Data */}
        <div className="space-y-3">
          <h4 className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">
            Original Data
          </h4>
          <div className="space-y-2 rounded-lg border bg-white p-4 dark:bg-slate-900">
            <DataField label="Outcome" value={callReview.original_outcome || 'N/A'} />
            {dataSections.map((section) =>
              section.original != null ? (
                <div key={section.key} className="mt-3 space-y-2">
                  <p className="text-muted-foreground text-xs font-medium">{section.label}</p>
                  <div className="text-sm">
                    <DataRenderer data={section.original} />
                  </div>
                </div>
              ) : null,
            )}
          </div>
        </div>

        {/* Reviewed Data */}
        <div className="space-y-3">
          <h4 className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">
            AI Reviewed Data
          </h4>
          <div className="space-y-4 rounded-lg border bg-white p-4 dark:bg-slate-900">
            <DataField
              label="Outcome"
              value={callReview.reviewed_outcome || 'N/A'}
              highlight={callReview.reviewed_outcome !== callReview.original_outcome}
            />
            {dataSections.map((section) =>
              section.reviewed != null ? (
                <div
                  key={section.key}
                  className="space-y-2 rounded-md border border-slate-200 p-3 dark:border-slate-700"
                >
                  <div className="flex items-center gap-2">
                    <p className="text-muted-foreground text-xs font-medium">{section.label}</p>
                    {section.matches === false && (
                      <Badge
                        variant="outline"
                        className="border-amber-300 bg-amber-50 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                      >
                        <AlertCircle className="mr-1 h-2 w-2" />
                        Mismatch
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm">
                    <DataRenderer data={section.reviewed} compareWith={section.original} />
                  </div>
                </div>
              ) : null,
            )}
          </div>
        </div>
      </div>

      {/* AI Analysis */}
      {callReview.ai_analysis && (
        <div className="space-y-2">
          <h4 className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">
            AI Analysis
          </h4>
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/30">
            <p className="text-sm whitespace-pre-wrap">{callReview.ai_analysis}</p>
          </div>
        </div>
      )}

      {/* Additional Review Data (custom fields) */}
      {callReview.additional_review_data &&
        Object.keys(callReview.additional_review_data).length > 0 && (
          <div className="space-y-2">
            <h4 className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">
              Additional Review Data
            </h4>
            <div className="rounded-lg border bg-white p-4 dark:border-gray-700 dark:bg-slate-900">
              <DataRenderer data={callReview.additional_review_data} />
            </div>
          </div>
        )}

      {/* Discrepancies */}
      {hasIssues && callReview?.discrepancies && callReview?.discrepancies.length > 0 && (
        <div className="space-y-2">
          <h4 className="flex items-center gap-2 text-sm font-semibold tracking-wide text-amber-700 uppercase">
            <AlertCircle className="h-4 w-4" />
            Discrepancies Found ({callReview.discrepancies.length})
          </h4>
          <div className="space-y-2">
            {callReview.discrepancies.map((discrepancy, idx) => (
              <div
                key={idx}
                className={`rounded-lg border p-3 ${
                  discrepancy.severity === 'high'
                    ? 'border-rose-200 bg-rose-50 dark:border-rose-800 dark:bg-rose-900/30'
                    : discrepancy.severity === 'medium'
                      ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/30'
                      : 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/30'
                }`}
              >
                <div className="mb-2 flex items-start justify-between">
                  <p className="text-sm font-semibold">{discrepancy.field}</p>
                  <Badge
                    variant="outline"
                    className={`text-xs ${
                      discrepancy.severity === 'high'
                        ? 'border-rose-300 bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-400'
                        : discrepancy.severity === 'medium'
                          ? 'border-amber-300 bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400'
                          : 'border-blue-300 bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400'
                    }`}
                  >
                    {discrepancy.severity}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <p className="text-muted-foreground mb-1">Original:</p>
                    <p className="rounded bg-white px-2 py-1 font-mono dark:bg-slate-900">
                      {String(discrepancy.original_value)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1">Reviewed:</p>
                    <p className="rounded bg-white px-2 py-1 font-mono dark:bg-slate-900">
                      {String(discrepancy.reviewed_value)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// --- Main component ---

export const CallReviewCard = ({
  callReview,
  compact = false,
}: {
  callReview: CallReview;
  compact?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const hasIssues =
    (callReview.discrepancies && callReview.discrepancies.length > 0) ||
    callReview.outcome_matches === false ||
    callReview.availability_matches === false ||
    callReview.lead_data_matches === false;

  const confidenceScore = callReview.confidence_score || 0;
  const callCreatedAt = callReview.call?.created_at || '';

  // If compact mode, just render the content without the outer Card and header
  if (compact) {
    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {hasIssues ? (
              <Badge
                variant="outline"
                className="border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
              >
                <AlertCircle className="mr-1 h-3 w-3" />
                Issues Found
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
              >
                <CheckCircle2 className="mr-1 h-3 w-3" />
                Verified
              </Badge>
            )}
          </div>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm">
              {isOpen ? (
                <>
                  <ChevronUp className="mr-1 h-4 w-4" />
                  Hide Details
                </>
              ) : (
                <>
                  <ChevronDown className="mr-1 h-4 w-4" />
                  View Details
                </>
              )}
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <div className="space-y-6">
            <ReviewContent
              callReview={callReview}
              confidenceScore={confidenceScore}
              hasIssues={hasIssues}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  // Regular (non-compact) mode - shows full Card with header
  return (
    <Card className={hasIssues ? 'border-2 border-amber-400' : 'border-2 border-emerald-400'}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="mb-2 flex items-center gap-2">
                <Phone className="text-muted-foreground h-4 w-4" />
                <CardTitle className="text-lg">Call #{callReview.call_id}</CardTitle>
                {hasIssues ? (
                  <Badge
                    variant="outline"
                    className="border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                  >
                    <AlertCircle className="mr-1 h-3 w-3" />
                    Issues Found
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                  >
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    Verified
                  </Badge>
                )}
              </div>
              <CardDescription className="flex items-center gap-2">
                <Clock className="h-3 w-3" />
                {callCreatedAt ? formatDateTime(callCreatedAt) : 'Unknown date'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm">
                  {isOpen ? (
                    <>
                      <ChevronUp className="mr-1 h-4 w-4" />
                      Hide Details
                    </>
                  ) : (
                    <>
                      <ChevronDown className="mr-1 h-4 w-4" />
                      View Details
                    </>
                  )}
                </Button>
              </CollapsibleTrigger>
              <Button
                className="ms-auto flex"
                onClick={() => {
                  navigate(`/calls/${callReview.call.id}`);
                }}
              >
                View Call
              </Button>
            </div>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="space-y-6">
            <ReviewContent
              callReview={callReview}
              confidenceScore={confidenceScore}
              hasIssues={hasIssues}
            />
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

interface DataFieldProps {
  label: string;
  value: string;
  highlight?: boolean;
  mono?: boolean;
}

const DataField = ({ label, value, highlight, mono }: DataFieldProps) => {
  return (
    <div className="space-y-1">
      <p className="text-muted-foreground text-xs font-medium">{label}</p>
      <p
        className={`text-sm ${mono ? 'font-mono text-xs' : ''} ${
          highlight
            ? 'rounded bg-amber-100 px-2 py-1 text-amber-900 dark:bg-amber-900/50 dark:text-amber-200'
            : ''
        }`}
      >
        {value}
      </p>
    </div>
  );
};
